import { observer } from 'mobx-react';
import React from 'react';
import { PromiseMessage, PromiseRetry } from '../../../lib/components';
import { ReactNodeGenerator } from '../../../lib/helpers';
import { AdminClassroomSceneStore } from './AdminClassroomSceneStore';
import { ClassroomAddControls } from './parts/ClassroomAddControls';
import { ClassroomDeleteControls } from './parts/ClassroomDeleteControls';
import { ClassroomEditControls } from './parts/ClassroomEditControls';
import { ChangeTimeSuggestionScene } from './parts/ChangeTimeSuggestionScene'
import { ClassroomEditForm } from './parts/ClassroomEditForm';
import { ClassroomSearchControls } from './parts/ClassroomSearchControls';
import { ClassroomSearchResults } from './parts/ClassroomSearchResults';
import { UserSearchControls } from './parts/UserSearchControls';
import { UserSearchResults } from './parts/UserSearchResults';
import { TeacherScheduleModal } from 'lib/components/TeacherSchedulerModal';

function* renderer(store: AdminClassroomSceneStore): ReactNodeGenerator {
  const lookupState = store.contentLookup.lookupState;
  yield (<PromiseMessage key="lookup-state"
    label="Loading lessons" state={lookupState} align="center" />);

  yield (<PromiseRetry key="lookup-retry"
    state={lookupState} retry={store.contentLookup.load} align="center" />);

  yield (<TeacherScheduleModal key="TeacherSchedule" />);

  if (lookupState?.isResolved !== true)
    return;

  yield (
    <ChangeTimeSuggestionScene key="classroom-add-controls" />
  );

  yield (
    <div className="row" key="classroom-search">
      <div className="col">
        <div className="card admin-classroom-scene classroom-search">
          <div className="card-header">
            <div className="row">
              <div className="col">
                Search classrooms
              </div>
              {store.mode === 'view' &&
                <div className="col-auto">
                  <button type="button" className="btn btn-primary"
                    onClick={store.beginAdd}>
                    Add
                  </button>
                </div>
              }
            </div>
          </div>
          <div className="card-body">
            <ClassroomSearchControls store={store} />
            <ClassroomDeleteControls store={store.deleteClassroom} />
            <PromiseMessage key="result-state"
              label="Loading"
              state={store.classroomSearch.resultState} align="center" />
            <ClassroomSearchResults store={store} />
          </div>
        </div>
      </div>
    </div>
  );

  yield (
    <div className="row" key="student-search">
      <div className="col">
        <div className="card admin-classroom-scene user-search student">
          <div className="card-header">
            Search students
          </div>
          <div className="card-body">
            <UserSearchControls store={store.studentSearch} />
            <PromiseMessage key="result-state"
              label="Loading" state={store.studentSearch.resultState}
              align="right" />
            <UserSearchResults store={store.studentSearch} />
          </div>
        </div>
      </div>
    </div>
  );

  yield (
    <div className="row" key="teacher-search">
      <div className="col">
        <div className="card admin-classroom-scene user-search teacher">
          <div className="card-header">
            Search teacher
          </div>
          <div className="card-body">
            <UserSearchControls store={store.teacherSearch} />
            <PromiseMessage key="result-state"
              label="Loading" state={store.teacherSearch.resultState}
              align="right" />
            <UserSearchResults store={store.teacherSearch} />
          </div>
        </div>
      </div>
    </div>
  );

  if (store.mode === 'add-classroom' && store.addClassroom !== null) {
    yield (
      <ClassroomEditForm store={store.addClassroom.form}
        title="Add classroom"
        key="classroom-add-form" />);
    yield (
      <ClassroomAddControls store={store.addClassroom}
        key="classroom-add-controls" />);
  }

  if (store.mode === 'edit-classroom' && store.editClassroom !== null) {
    yield (
      <ClassroomEditForm store={store.editClassroom.form}
        title="Edit classroom"
        key="classroom-edit-form" />
    );
    yield (
      <ClassroomEditControls store={store.editClassroom}
        key="classroom-add-controls" />);
  }

}

@observer
export class AdminClassroomScene extends React.Component {
  store: AdminClassroomSceneStore;

  constructor(props: any) {
    super(props);
    this.store = new AdminClassroomSceneStore();
  }

  componentDidMount() {
    this.store.contentLookup.load();
  }

  render() {
    return Array.from(renderer(this.store));
  }

  componentWillUnmount() {
    this.store.dispose();
  }
}
